import styled from "@emotion/styled"
import { PTextarea } from "@porsche-design-system/components-react"
import type { FormState, TextareaInputEventDetail } from "@porsche-design-system/components-react"
import debounce from "lodash-es/debounce"
import { forwardRef, useCallback, useEffect, useMemo, useState } from "react"
import { Tooltip } from "../../tooltip/Tooltip"

export type TextAreaValue = string | undefined

export interface TextAreaProps {
	className?: string
	debounced?: number
	descriptionText?: string
	dir?: HTMLElement["dir"]
	disabledHint?: string
	errorMessage?: string
	hideLabel?: boolean
	label?: string
	maxLength?: number
	name: string
	onChange?: (value: TextAreaValue) => void
	placeholder?: string
	required?: boolean
	rows?: number
	state?: FormState
	value?: TextAreaValue
}

export const TextArea = forwardRef<HTMLDivElement, TextAreaProps>((props, ref) => {
	const {
		className,
		debounced,
		descriptionText,
		dir,
		disabledHint,
		errorMessage,
		label,
		onChange,
		placeholder,
		rows,
		state,
		value,
		...rest
	} = props

	const [localValue, setLocalValue] = useState<TextAreaValue>(value ?? "")
	const [hasPendingChanges, setHasPendingChanges] = useState(false)

	useEffect(() => {
		if (debounced) {
			debounce(() => {
				setLocalValue(value)
			}, debounced)
		} else {
			setLocalValue(value)
		}
	}, [value, hasPendingChanges, debounced])

	const onChangeDebounced = useMemo(
		() =>
			debounce((newValue: TextAreaValue) => {
				setHasPendingChanges(false)
				onChange?.(newValue)
			}, debounced),
		[debounced, onChange],
	)

	const handleChangeDebounced = useCallback(
		(newValue: TextAreaValue) => {
			setHasPendingChanges(true)
			onChangeDebounced(newValue)
		},
		[onChangeDebounced],
	)

	const handleChange = (event: CustomEvent<TextareaInputEventDetail>) => {
		if (disabledHint ?? !onChange) {
			return
		}
		const { value: newValue } = event.target as HTMLTextAreaElement
		setLocalValue(newValue)
		const callback = debounced ? handleChangeDebounced : onChange
		callback(newValue === "" ? undefined : newValue)
	}

	const input = (
		<StyledTextArea
			{...rest}
			ref={ref}
			className={className}
			state={errorMessage && !hasPendingChanges ? "error" : state}
			label={label}
			description={descriptionText}
			placeholder={placeholder}
			message={errorMessage && !hasPendingChanges ? errorMessage : undefined}
			dir={dir}
			onInput={handleChange}
			value={localValue ?? ""}
			disabled={!!disabledHint || disabledHint === ""}
			counter={!!rest.maxLength}
			rows={rows}
		/>
	)

	return <Tooltip content={disabledHint}>{input}</Tooltip>
})

const StyledTextArea = styled(PTextarea)<{ rows?: number }>`
	--p-textarea-field-sizing: ${({ rows }) => (rows ? "initial" : "content")};
	--p-textarea-min-height: ${({ rows }) => (rows ? "initial" : "196px")};
`

TextArea.displayName = "TextArea"
